import type { AnyFieldApi } from "@tanstack/react-form";
import {
	FieldComponents as BaseFieldComponents,
	FormComponents as BaseFormComponents,
	ErrorMessages,
} from "@bidbattles/bloks/components/form";

import { useFieldContext } from "@bidbattles/bloks/hooks/form-context";
import { inputWrapper } from "@bidbattles/bloks/components/ui/input";
import { useId } from "react";
import {
	Mollie,
	type MollieComponentProps,
	type MollieReactComponent,
} from "@/lib/mollie/components";
import { cn } from "@bidbattles/bloks/lib/ui";
import type { MollieComponentType } from "@/lib/mollie/types";

function handleMollieError<Field extends AnyFieldApi>(field: Field) {
	return (message: string | null) => {
		field.setErrorMap({
			onChange: message ?? undefined,
		});
	};
}

const mollieComponents = {
	card: Mollie.Card,
	cardNumber: Mollie.CardNumber,
	cardHolder: Mollie.CardHolder,
	expiryDate: Mollie.ExpiryDate,
	verificationCode: Mollie.VerificationCode,
} as const satisfies Record<MollieComponentType, React.FC>;

function createMollieComponent<Type extends MollieComponentType>(type: Type) {
	const Component = mollieComponents[type] as MollieReactComponent<Type>;

	return () => {
		const labels = {
			card: "Card",
			cardNumber: "Card Number",
			cardHolder: "Card Holder",
			expiryDate: "Expiry",
			verificationCode: "CVV",
		};
		const id = `form-${type}:${useId()}`;
		const field = useFieldContext();
		const cls = inputWrapper({ variant: "box" });
		const props: MollieComponentProps<Type> = {
			className: cn(
				cls,
				"[&_.mollie-component]:py-2 [&_.mollie-component]:px-2.5 [&_.mollie-component]:w-full",
				"has-[.has-focus]:shadow-accent-primary/50 has-[.has-focus]:border-accent-primary",
			),
			onValidate: handleMollieError(field),
		};
		return (
			<div className="flex flex-col gap-2">
				<label htmlFor={id} className="text-gray-500 text-sm md:text-base">
					{labels[type]}
				</label>
				<div className="w-full">
					<Component {...props} />
					<ErrorMessages errorMap={field.state.meta.errorMap} />
				</div>
			</div>
		);
	};
}

const FieldComponents = {
	...BaseFieldComponents,
	CardNumber: createMollieComponent("cardNumber"),
	CardHolder: createMollieComponent("cardHolder"),
	ExpiryDate: createMollieComponent("expiryDate"),
	VerificationCode: createMollieComponent("verificationCode"),
};
const FormComponents = BaseFormComponents;

export { FieldComponents, FormComponents };
